import { GoogleCalendarCard } from "@/components/GoogleCalendarCard";
import { colorHex } from "@/lib/calendarColors";
import { DoorCode } from "@/components/DoorCode";
import { StudioDevicesCard } from "@/components/StudioDevicesCard";
import { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { RescheduleDialog } from "@/components/RescheduleDialog";
import { Calendar } from "@/components/ui/calendar";
import { CalendarDays, Clock, Phone, Mail, CalendarClock } from "lucide-react";
import { api, fmtMoney, fmtTimeStr } from "@/lib/api";

function fmtDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const STATUS_COLOR = {
  pending_payment: "text-amber-400 border-amber-400/30",
  booked: "text-green-400 border-green-400/30",
  completed: "text-blue-400 border-blue-400/30",
  cancelled: "text-red-400 border-red-400/30",
};

export default function AdminCalendar() {
  const [selected, setSelected] = useState(new Date());
  const qc = useQueryClient();
  const [rescheduling, setRescheduling] = useState(null);
  const { data: bookings = [] } = useQuery({ queryKey: ["admin-bookings"], queryFn: () => api.get("/bookings").then((r) => r.data) });

  const { data: svcList = [] } = useQuery({ queryKey: ["services-admin"], queryFn: () => api.get("/services?include_inactive=true").then((r) => r.data) });
  const colorOf = Object.fromEntries(svcList.map((s) => [s.id, s.calendar_color]));
  const byDate = useMemo(() => {
    const m = {};
    bookings.forEach((b) => {
      if (b.payment_status !== "paid" || ["cancelled", "slot_conflict"].includes(b.status)) return;
      (m[b.event_date] = m[b.event_date] || []).push(b);
    });
    return m;
  }, [bookings]);

  const selStr = fmtDate(selected);
  const dayBookings = (byDate[selStr] || []).slice().sort((a, b) => (a.start_time || 0) - (b.start_time || 0));

  return (
    <div data-testid="admin-calendar">
      <h1 className="font-display text-3xl text-white mb-1">Booking Calendar</h1>
      <p className="text-white/50 text-sm mb-6">Shows confirmed paid bookings only. Gold dots mark booked days — tap a day to see its schedule.</p>
      <GoogleCalendarCard />
      <StudioDevicesCard />

      <div className="grid md:grid-cols-2 gap-8">
        <div className="glass rounded-2xl p-4 inline-block">
          <Calendar
            mode="single"
            selected={selected}
            onSelect={(d) => d && setSelected(d)}
            modifiers={{ hasBookings: (d) => !!byDate[fmtDate(d)]?.length }}
            modifiersClassNames={{ hasBookings: "relative after:content-[''] after:absolute after:bottom-1 after:left-1/2 after:-translate-x-1/2 after:h-1 after:w-1 after:rounded-full after:bg-[#D4AF37]" }}
            className="text-white"
          />
        </div>

        <div>
          <div className="flex items-center gap-2 mb-4">
            <CalendarDays size={18} className="text-[#D4AF37]" />
            <h2 className="font-display text-xl text-white">{selected.toDateString()}</h2>
            <span className="text-white/40 text-sm">· {dayBookings.length} booking{dayBookings.length === 1 ? "" : "s"}</span>
          </div>

          {dayBookings.length === 0 ? (
            <p className="text-white/40 text-sm">No bookings on this day.</p>
          ) : (
            <div className="space-y-3">
              {dayBookings.map((b, i) => (
                <div key={b.id} className="bg-[#111] border border-white/10 border-l-4 rounded-xl p-4" style={{ borderLeftColor: colorHex(colorOf[b.service_id]) }} data-testid={`cal-booking-${i}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-white font-display flex items-center gap-2">
                        <Clock size={14} className="text-[#D4AF37]" /> {fmtTimeStr(b.event_time)} · {b.hours}h
                      </p>
                      <p className="text-white/70 text-sm mt-1 flex items-center gap-1.5"><span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: colorHex(colorOf[b.service_id]) }} />{b.name} — {b.service_title}</p>
                      <div className="flex flex-wrap gap-3 mt-2">
                        <a href={`tel:${b.phone}`} className="text-xs flex items-center gap-1 text-white/50 hover:text-[#D4AF37]"><Phone size={12} /> {b.phone}</a>
                        <a href={`mailto:${b.email}`} className="text-xs flex items-center gap-1 text-white/50 hover:text-[#D4AF37]"><Mail size={12} /> {b.email}</a>
                        <DoorCode booking={b} testid={`cal-door-code-${i}`} />
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <span className={`text-[10px] uppercase border rounded-full px-2 py-0.5 ${STATUS_COLOR[b.status] || "text-white/60 border-white/20"}`}>{b.status.replace("_", " ")}</span>
                      <p className={`text-xs mt-1.5 ${b.payment_status === "paid" ? "text-green-400" : "text-white/40"}`}>{b.payment_status === "paid" ? (b.total_cents != null ? fmtMoney(b.total_cents) : "paid") : "unpaid"}</p>
                      {b.status !== "cancelled" && <button onClick={() => setRescheduling(b)} data-testid={`cal-reschedule-${i}`} className="text-xs mt-2 inline-flex items-center gap-1 text-white/50 hover:text-[#D4AF37]"><CalendarClock size={12} /> Reschedule</button>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <RescheduleDialog booking={rescheduling} onClose={() => setRescheduling(null)} onDone={() => qc.invalidateQueries({ queryKey: ["admin-bookings"] })} />
    </div>
  );
}
